import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useRequestPasswordReset } from '../api'
import {
  getForgotPasswordServerEmailError,
} from '../hooks/useForgotPasswordEmailField'
import { readForgotPasswordEmailFromState } from '../lib'
import { BackToLoginButton } from '../components'
import {
  forgotPasswordFieldErrorSx,
  forgotPasswordHeadingSx,
  forgotPasswordLogoBlockSx,
  forgotPasswordPrimaryButtonSx,
  forgotPasswordSentCardSx,
  forgotPasswordSentHeadingFrameSx,
  forgotPasswordSentSubtitleContainerSx,
  forgotPasswordSentSubtitleSx,
  forgotPasswordSentTitleBlockSx,
} from '../styles/forgotPasswordStyles'

/** Password reset email sent confirmation (Figma 5074-12051 / 5074-12053, TC-SPW3-14). */
export function ForgotPasswordEmailSentPage() {
  useDocumentTitle('A Password Reset Link Has Been Sent')
  const navigate = useNavigate()
  const location = useLocation()
  const email = readForgotPasswordEmailFromState(location.state)
  const resetRequest = useRequestPasswordReset()
  const [resendError, setResendError] = useState<string | null>(null)

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password', { replace: true })
    }
  }, [email, navigate])

  if (!email) {
    return null
  }

  const handleResend = () => {
    setResendError(null)
    resetRequest.mutate(
      { email },
      {
        onError: (error) => {
          setResendError(getForgotPasswordServerEmailError(error))
        },
      }
    )
  }

  return (
    <Box data-testid="forgot-password-sent-page" sx={forgotPasswordSentCardSx}>
      {/* Figma 5074-12054 (logo; inner 5074-12056 = 48px asset region) */}
      <Box sx={forgotPasswordLogoBlockSx} data-testid="forgot-password-sent-logo">
        <Box
          component="img"
          src={brushfireLogo}
          alt="Brushfire"
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>

      {/* Figma 5074-12057 — 5074-12059 heading + 5074-12060/12061 subtitle */}
      <Box sx={forgotPasswordSentTitleBlockSx} data-testid="forgot-password-sent-title-block">
        <Box sx={forgotPasswordSentHeadingFrameSx} data-testid="forgot-password-sent-heading">
          <Typography component="h1" sx={forgotPasswordHeadingSx} data-testid="forgot-password-sent-heading-text">
            A Password Reset Link Has Been Sent
          </Typography>
        </Box>
        <Box sx={forgotPasswordSentSubtitleContainerSx} data-testid="forgot-password-sent-subtitle-container">
          <Typography component="p" sx={forgotPasswordSentSubtitleSx} data-testid="forgot-password-sent-subtitle">
            A password reset link has been sent to {email}.
          </Typography>
        </Box>
      </Box>

      {resendError && (
        <Typography
          role="alert"
          data-testid="forgot-password-sent-resend-error"
          sx={{ ...forgotPasswordFieldErrorSx, pl: '5px', flexShrink: 0 }}
        >
          {resendError}
        </Typography>
      )}

      {/* Figma 5074-13030 */}
      <Button
        type="button"
        variant="contained"
        fullWidth
        disableElevation
        disabled={resetRequest.isPending}
        onClick={handleResend}
        data-testid="forgot-password-sent-send-email"
        sx={{ ...forgotPasswordPrimaryButtonSx, flexShrink: 0 }}
      >
        {resetRequest.isPending ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'Send Email'
        )}
      </Button>

      {/* Figma 5074-13036 */}
      <BackToLoginButton variant="outlined" />
    </Box>
  )
}
